import { createRouter, createWebHashHistory } from 'vue-router';
import StaticProxy from '@wiwdata/static-proxy';
import AccountView from '@/views/AccountView/AccountView.vue';
import AddCreditView from '@/views/AddCreditView.vue';
import AddOverrideView from '@/views/AddOverrideView.vue';
import AddTransactionView from '@/views/AddTransactionView/AddTransactionView.vue';
import ClearBillingView from '@/views/ClearBillingView.vue';
import CustomPlanView from '@/views/CustomPlanView.vue';
import BillingDayView from '@/views/BillingDayView/BillingDayView.vue';
import BillingDiscountView from '@/views/BillingDiscountView.vue';
import ExtendTrialView from '@/views/ExtendTrialView.vue';
import ChangePlanExpires from '@/views/ChangePlanExpires.vue';
import ChangeAttendanceExpires from '@/views/ChangeAttendanceExpires.vue';
import HomeView from '@/views/HomeView/HomeView.vue';
import LoginView from '@/views/LoginView.vue';
import RefundView from '@/views/RefundView.vue';
import RevenueDates from '@/views/RevenueDates.vue';
import SearchView from '@/views/SearchView.vue';
import TransferAccount from '@/views/TransferAccount.vue';
import DeleteUndeleteAccount from '@/views/DeleteUndeleteAccount.vue';
import SetBillingStatus from '@/views/SetBillingStatus.vue';
import TaxExemptStatusView from '@/views/TaxExemptStatusView.vue';
import AccountOverrides from '@/views/AccountOverrides.vue';
import ReactivateAccount from '@/views/ReactivateAccount.vue';
import SkipBilling from '@/views/SkipBilling.vue';
import http from '@/services/http';
import RoleActionMappingsView from '@/views/RoleActionMappingsView/RoleActionMappingsView.vue';
import ChangeTextCredit from '@/views/ChangeTextCredit.vue';
import ResumeBilling from '@/views/ResumeBilling.vue';
import ManageTrial from '@/views/ManageTrial.vue';
import ManageTrialSubscription from '@/views/ManageTrialSubscription.vue';
import StartTrialSubscription from '@/views/StartTrialSubscription.vue';
import SetParent from '@/views/SetParent.vue';
import SetPricingGroup from '@/views/SetPricingGroup.vue';
import SimplePlanChange from '@/views/SimplePlanChange.vue';
import ResetBillingEngine from '@/views/ResetBillingEngine.vue';
import CreateBackdatedSubscription from '@/views/CreateBackdatedSubscription.vue';
import UpdateTaxRate from '@/views/UpdateTaxRate.vue';
import PayrollSyncUsers from '@/views/PayrollSyncUsers.vue';
import PayrollSyncWorkplaces from '@/views/PayrollSyncWorkplaces.vue';
import MigrateBillingEngine from '@/views/MigrateBillingEngine.vue';

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      name: 'HomeView',
      component: HomeView,
    },
    {
      path: '/login:catchAll(.*)',
      name: 'Login',
      component: LoginView,
    },
    {
      path: '/accounts/:accountId',
      redirect: () => ({ name: 'AccountViewPanel', params: { panel: 'details' } }),
    },
    {
      path: '/accounts/:accountId/actions/add-transaction',
      name: 'addTransaction',
      component: AddTransactionView,
      props: true,
    },
    {
      path: '/accounts/:accountId/actions/add-credit',
      name: 'addCredit',
      component: AddCreditView,
    },
    {
      path: '/accounts/:accountId/actions/reactivate-account',
      name: 'ReactivateAccount',
      component: ReactivateAccount,
    },
    {
      path: '/accounts/:accountId/actions/add-override',
      name: 'addOverride',
      component: AddOverrideView,
    },
    {
      path: '/accounts/:accountId/actions/custom-plan',
      name: 'customPlan',
      component: CustomPlanView,
    },
    {
      path: '/accounts/:accountId/actions/extend-trial',
      name: 'extendTrial',
      component: ExtendTrialView,
    },
    {
      path: '/accounts/:accountId/actions/change-plan-expires',
      name: 'changePlanExpires',
      component: ChangePlanExpires,
    },
    {
      path: '/accounts/:accountId/actions/change-attendance-expires',
      name: 'changeAttendanceExpires',
      component: ChangeAttendanceExpires,
    },
    {
      path: '/accounts/:accountId/actions/clear-billing',
      name: 'clearBilling',
      component: ClearBillingView,
    },
    {
      path: '/accounts/:accountId/actions/free-to-enterprise-trial',
      redirect: '/accounts/:accountId/actions/manage-trial',
    },
    {
      path: '/accounts/:accountId/actions/manage-trial',
      name: 'manageTrial',
      component: ManageTrial,
    },
    {
      path: '/accounts/:accountId/actions/manage-trial-subscription',
      name: 'manageTrialSubscription',
      component: ManageTrialSubscription,
    },
    {
      path: '/accounts/:accountId/actions/start-trial-subscription',
      name: 'startTrialSubscription',
      component: StartTrialSubscription,
    },
    {
      path: '/accounts/:accountId/actions/set-parent',
      name: 'setParent',
      component: SetParent,
    },
    {
      path: '/accounts/:accountId/actions/set-pricing-group',
      name: 'setPricingGroup',
      component: SetPricingGroup,
    },
    {
      path: '/accounts/:accountId/actions/simple-plan-change',
      name: 'simplePlanChange',
      component: SimplePlanChange,
    },
    {
      path: '/accounts/:accountId/actions/billing-day',
      name: 'billingDay',
      component: BillingDayView,
    },
    {
      path: '/accounts/:accountId/actions/revenue-dates',
      name: 'revenueDates',
      component: RevenueDates,
    },
    {
      path: '/accounts/:accountId/actions/set-billing-status',
      name: 'setBillingStatus',
      component: SetBillingStatus,
    },
    {
      path: '/accounts/:accountId/actions/delete-undelete-account',
      name: 'deleteUndeleteAccount',
      component: DeleteUndeleteAccount,
    },
    {
      path: '/accounts/:accountId/actions/transfer-account',
      name: 'transferAccount',
      component: TransferAccount,
    },
    {
      path: '/accounts/:accountId/actions/set-discount',
      name: 'billingDiscount',
      component: BillingDiscountView,
    },
    {
      path: '/accounts/:accountId/actions/tax-exempt',
      name: 'taxExempt',
      component: TaxExemptStatusView,
    },
    {
      path: '/accounts/:accountId/actions/change-text-credits',
      name: 'changeTextCredits',
      component: ChangeTextCredit,
    },
    {
      path: '/accounts/:accountId/actions/skip-billing',
      name: 'skipBilling',
      component: SkipBilling,
    },
    {
      path: '/accounts/:accountId/actions/covid-emails',
      name: 'ResumeBilling',
      component: ResumeBilling,
    },
    {
      path: '/accounts/:accountId/actions/reset-billing-engine',
      name: 'ResetBillingEngine',
      component: ResetBillingEngine,
    },
    {
      path: '/accounts/:accountId/actions/migrate-billing-engine',
      name: 'MigrateBillingEngine',
      component: MigrateBillingEngine,
    },
    {
      path: '/accounts/:accountId/actions/create-backdated-subscription',
      name: 'CreateBackdatedSubscription',
      component: CreateBackdatedSubscription,
    },
    {
      path: '/accounts/:accountId/actions/update-tax-rate',
      name: 'UpdateTaxRate',
      component: UpdateTaxRate,
    },
    {
      path: '/accounts/:accountId/actions/payroll-sync-users',
      name: 'PayrollSyncUsers',
      component: PayrollSyncUsers,
    },
    {
      path: '/accounts/:accountId/actions/payroll-sync-workplaces',
      name: 'PayrollSyncWorkplaces',
      component: PayrollSyncWorkplaces,
    },
    {
      path: '/accounts/:accountId/transactions/:transactionId/refund',
      name: 'refund',
      component: RefundView,
    },
    {
      path: '/accounts/:accountId/overrides',
      name: 'overrides',
      component: AccountOverrides,
    },
    {
      path: '/search',
      name: 'search',
      component: SearchView,
    },
    {
      path: '/accounts/:accountId/sections/:panel',
      name: 'AccountViewPanel',
      component: AccountView,
      props: true,
    },
    {
      path: '/role-action-mappings',
      name: 'roleActionMappingsView',
      component: RoleActionMappingsView,
    },
    {
      path: '/*', redirect: '/',
    },
  ],
});

/**
 * Executes before changing routes and confirms that the user is currently logged in
 * to access the backend content. All backend calls require a valid authentication token
 * provided by Google, so this is not a security measure, but a UX feature to make sure
 * users coming from any location in the app see the login view prior to any other attempts
 * to use the app.
 *
 * @param to
 *  The route the user requested navigation to
 * @param from
 *  The current route that the user is leaving
 * @param next
 *  The resolution function to call depending on how the redirection should be carried out.
 *  See the Vue Router documentation for more details.
 */
function loggedInCheck(to, from, next) {
  const loggedIn = StaticProxy.isLoggedIn();
  const toLogin = to.path.startsWith('/login');

  if (loggedIn) {
    return toLogin ? next('/') : next();
  }

  if (!toLogin) {
    return next(`/login${to.path || '/'}`);
  }

  return next();
}
router.beforeEach(loggedInCheck);

/**
 * For the accountId given, get the account's company name and set it as the
 * document title. The document title is the name that appears in the browser
 * window's title.
 *
 * @param accountId
 *  The accountId to get the account title for.
 * @returns {Promise<void>}
 */
async function setAccountTitle(accountId) {
  const response = await http.getAccountDetails(accountId);
  document.title = `${response.payload.data.company} | Finance`;
}

/**
 * Identifies the title to set as the page document title. When an accountId is
 * provided, the account name will be set as the document title, otherwise the
 * title will be the default 'Finance | When I Work'.
 *
 * @param toRoute
 *  The route that the user requested navigation to.
 * @returns {Promise<void>}
 */
async function setTitle(toRoute, fromRoute) {
  const toAccountId = toRoute.params.accountId;
  const sameAccount = fromRoute.params.accountId === toAccountId;
  if (!toAccountId) {
    document.title = 'Finance | When I Work';
  } else if (!sameAccount) {
    await setAccountTitle(toAccountId);
  }
}
router.afterEach(setTitle);

export default router;
